import {
  ClickAwayListener,
  CloseIcon,
  Divider,
  Drawer,
  Flex,
  IconButton,
  InfiniteScroll,
  Loading,
  Text,
  useTheme,
} from '@shared/components';
import { useMediaQuery } from '@shared/hooks';

import { NotificationItem, NotificationNoRowOverlay } from '../../components';
import { MENU_DRAWER_WIDTH_LARGE, MENU_DRAWER_WIDTH_SMALL } from '../../constants';
import { useGetMyNotifications } from '../../hooks';

interface NotificationDrawerProps {
  isOpen: boolean;
  shouldMinify?: boolean;
  close: () => void;
}

export default function NotificationDrawer(props: Readonly<NotificationDrawerProps>) {
  const { isOpen, shouldMinify, close } = props;

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('desktop'));

  const {
    notificationsData: notificationRecipients,
    isInitialLoading,
    isFetchingMore,
    hasMore,
    handleFetchMore,
  } = useGetMyNotifications();

  return (
    <ClickAwayListener
      onClickAway={close}
      mouseEvent={isOpen ? 'onMouseUp' : false}
      touchEvent={isOpen ? 'onTouchStart' : false}
    >
      <Drawer
        variant='persistent'
        anchor='left'
        sx={(theme) => ({
          '& .MuiDrawer-paper': {
            position: 'fixed',
            top: 0,
            left: `${shouldMinify ? MENU_DRAWER_WIDTH_SMALL : MENU_DRAWER_WIDTH_LARGE}px`,
            height: '100dvh',
            width: '512px',
            boxShadow: 'none',
            zIndex: theme.zIndex.drawer - 1,
            transition: theme.transitions.create(['left'], {
              duration: isOpen
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
              easing: theme.transitions.easing.sharp,
            }),
          },
        })}
        open={isOpen && isDesktop}
        onClose={close}
        ModalProps={{ keepMounted: true }}
        transitionDuration={{ enter: 225, exit: 195 }}
      >
        <Flex
          sx={(theme) => ({
            alignItems: 'center',
            justifyContent: 'space-between',
            p: theme.spacing(6),
          })}
        >
          <Text variant='h3'>การแจ้งเตือน</Text>
          <IconButton onClick={close}>
            <CloseIcon />
          </IconButton>
        </Flex>
        <Divider />
        {isInitialLoading ? (
          <Loading sx={{ height: '100%' }} />
        ) : (
          <Flex sx={{ flexDirection: 'column', overflowX: 'hidden', width: '100%' }}>
            {notificationRecipients.length > 0 ? (
              <InfiniteScroll
                hasMore={hasMore}
                handleFetch={handleFetchMore}
                loading={isFetchingMore}
                loader={<Loading />}
                maxHeight='100dvh'
              >
                {notificationRecipients.map((recipient) => (
                  <NotificationItem
                    notificationRecipient={recipient}
                    key={recipient.id}
                    close={close}
                  />
                ))}
              </InfiniteScroll>
            ) : (
              <NotificationNoRowOverlay />
            )}
          </Flex>
        )}
      </Drawer>
    </ClickAwayListener>
  );
}
